import type {
  BankNotification,
  MerchantProfile,
  Overview,
  Payment,
  PaymentList,
  WebhookDelivery,
} from './types';

const BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const TOKEN_KEY = 'owner-session';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** ຂໍ້ຄວາມພາສາລາວຕາມລະຫັດທີ່ API ຄືນມາ */
const MESSAGE: Record<string, string> = {
  INVALID_CREDENTIALS: 'ຊື່ຜູ້ໃຊ້ ຫຼື ລະຫັດຜ່ານບໍ່ຖືກຕ້ອງ',
  TOO_MANY_ATTEMPTS: 'ລອງຜິດຫຼາຍເທື່ອ — ລໍຖ້າ 15 ນາທີ',
  UNAUTHORIZED: 'ໝົດເວລາເຂົ້າສູ່ລະບົບ — ກະລຸນາເຂົ້າໃໝ່',
  INVALID_REQUEST: 'ຂໍ້ມູນບໍ່ຖືກຕ້ອງ',
  INVALID_QR: 'QR ບໍ່ຖືກຕ້ອງ — ກວດວ່າ copy ມາຄົບບໍ່',
  MERCHANT_NOT_SET_UP: 'ຍັງບໍ່ໄດ້ຕັ້ງຄ່າບັນຊີຮັບເງິນ',
  MERCHANT_ALREADY_SET_UP: 'ຕັ້ງຄ່າບັນຊີຮັບເງິນໄວ້ແລ້ວ',
  NOT_PENDING: 'ລາຍການນີ້ບໍ່ໄດ້ລໍຖ້າຈ່າຍແລ້ວ',
  PAYMENT_NOT_FOUND: 'ບໍ່ພົບລາຍການນີ້',
  TOO_MANY_PENDING_WITH_SAME_AMOUNT: 'ມີລາຍການຄ້າງທີ່ຍອດໃກ້ຄຽງກັນຫຼາຍເກີນໄປ',
  INVALID_AMOUNT: 'ຈຳນວນເງິນບໍ່ຖືກຕ້ອງ',
  DELIVERY_NOT_FOUND: 'ບໍ່ພົບລາຍການ webhook',
};

function readStoredToken(): string {
  try {
    return sessionStorage.getItem(TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

let token = readStoredToken();

/** token ເກັບໃນ sessionStorage — ປິດແທັບແລ້ວຕ້ອງເຂົ້າໃໝ່ */
export const session = {
  has: () => token !== '',
  set(value: string): void {
    token = value;
    try {
      sessionStorage.setItem(TOKEN_KEY, value);
    } catch {
      /* ຈື່ບໍ່ໄດ້ ແຕ່ຍັງໃຊ້ໄດ້ຈົນກວ່າຈະປິດແທັບ */
    }
  },
  clear(): void {
    token = '';
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ບໍ່ເປັນຫຍັງ */
    }
  },
};

/** ຖືກເອີ້ນເມື່ອ session ໝົດອາຍຸ — App ຈະພາໄປໜ້າເຂົ້າສູ່ລະບົບ */
let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/admin/v1${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError('ຕິດຕໍ່ເຊີບເວີບໍ່ໄດ້', 0, 'NETWORK');
  }

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const field = (name: string) =>
      body !== null && typeof body === 'object' && name in body
        ? String((body as Record<string, unknown>)[name])
        : '';
    const code = field('message') || 'ERROR';
    const detail = field('detail');

    if (response.status === 401 && path !== '/login') {
      session.clear();
      onUnauthorized();
    }
    const message = MESSAGE[code] ?? 'ເກີດຂໍ້ຜິດພາດ';
    throw new ApiError(detail ? `${message} (${detail})` : message, response.status, code);
  }
  return response;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  return (await send(path, init)).json() as Promise<T>;
}

const post = (body?: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body ?? {}) });

export interface ListParams {
  status?: string;
  q?: string;
  from?: string;
  to?: string;
  page?: number;
}

function query(params: ListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

export type SecretName = 'secret-key' | 'webhook-secret' | 'notify-token';

export const api = {
  login: (username: string, password: string) =>
    call<{ token: string; expiresAt: string }>('/login', post({ username, password })),
  session: () => call<{ username: string; appName: string }>('/session'),

  overview: () => call<Overview>('/overview'),
  payments: (params: ListParams) => call<PaymentList>(`/payments${query(params)}`),
  payment: (id: string) =>
    call<{ payment: Payment; deliveries: WebhookDelivery[] }>(`/payments/${encodeURIComponent(id)}`),
  createPayment: (input: { amount: number; description: string; orderNo?: string; expiryMinutes?: number }) =>
    call<{ payment: Payment }>('/payments', post(input)),
  confirm: (id: string, note: string) =>
    call<{ payment: Payment }>(`/payments/${encodeURIComponent(id)}/confirm`, post({ note })),
  cancel: (id: string, note: string) =>
    call<{ payment: Payment }>(`/payments/${encodeURIComponent(id)}/cancel`, post({ note })),

  /** ດາວໂຫຼດ CSV ຜ່ານ fetch ເພາະຕ້ອງແນບ token */
  async exportCsv(params: ListParams): Promise<void> {
    const { page: _page, ...filters } = params;
    const response = await send(`/payments/export.csv${query(filters)}`);
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  },

  notifications: () => call<{ notifications: BankNotification[] }>('/notifications'),
  webhooks: () => call<{ deliveries: WebhookDelivery[] }>('/webhooks'),
  retryWebhook: (id: number) => call<{ message: string }>(`/webhooks/${id}/retry`, post()),

  merchant: () => call<{ merchant: MerchantProfile | null }>('/merchant'),
  setupMerchant: (input: {
    name: string;
    laoQrPayload: string;
    destinationAccount: string;
    accountName?: string;
    webhookUrl?: string;
  }) =>
    call<{ merchant: MerchantProfile; secretKey: string; webhookSecret: string }>(
      '/merchant',
      post(input),
    ),
  updateMerchant: (changes: {
    name?: string;
    laoQrPayload?: string;
    destinationAccount?: string;
    accountName?: string | null;
    webhookUrl?: string | null;
  }) =>
    call<{ merchant: MerchantProfile }>('/merchant', {
      method: 'PATCH',
      body: JSON.stringify(changes),
    }),
  rotate: (what: SecretName) =>
    call<{ secretKey?: string; webhookSecret?: string; notifyToken?: string }>(
      `/merchant/rotate/${what}`,
      post(),
    ),
};
